import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Loader2, Search } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import {
	audienceKeys,
	bulkTagMembers,
	listAudience,
	plural,
	tagMembersByList,
} from "@/entities/audience"
import { useDebounced } from "@/shared/hooks/use-debounced"
import { Button } from "@/shared/ui/button"
import { Checkbox } from "@/shared/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Input } from "@/shared/ui/input"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Reveal } from "@/shared/ui/reveal"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { Skeleton } from "@/shared/ui/skeleton"
import { Textarea } from "@/shared/ui/textarea"

const PAGE_SIZE = 30
/** Столько же принимает api в sessionIds. */
const MAX_PICKED = 1000
/** Столько принимает api в одном списке. */
const MAX_LISTED = 2000
const PEOPLE: [string, string, string] = ["человек", "человека", "человек"]

type Mode = "search" | "list"

interface Tag {
	id: string
	name: string
}

/** Добавить людей в группу: найти и отметить или вставить список @username и ID. */
export function AddMembersDialog({
	botId,
	tag,
	open,
	onOpenChange,
}: {
	botId: string
	tag: Tag
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const [mode, setMode] = useState<Mode>("search")
	const close = () => onOpenChange(false)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Добавить людей в «{tag.name}»</DialogTitle>
					<DialogDescription>
						{mode === "search"
							? "Найдите по имени, @username или ID и отметьте галочками. Кто уже в группе, не задвоится."
							: "Вставьте @username, ссылки t.me или ID, по одному в строке или через запятую. Найдём и тех, кто ещё не писал боту."}
					</DialogDescription>
				</DialogHeader>
				<SegmentedControl<Mode>
					aria-label="Как добавить"
					value={mode}
					onChange={(next) => next && setMode(next)}
					options={[
						{ value: "search", label: "Поиск" },
						{ value: "list", label: "Списком" },
					]}
				/>
				<LoadSwap loading={false} id={mode}>
					{mode === "search" ? (
						<SearchPane botId={botId} tag={tag} open={open} onDone={close} />
					) : (
						<ListPane botId={botId} tag={tag} open={open} onDone={close} />
					)}
				</LoadSwap>
			</DialogContent>
		</Dialog>
	)
}

function useRefresh(botId: string) {
	const queryClient = useQueryClient()
	return async () => {
		await queryClient.invalidateQueries({ queryKey: ["bot-builder", "audience", botId] })
		await queryClient.invalidateQueries({ queryKey: ["bot-builder", "stats", botId, "tags"] })
	}
}

function SearchPane({
	botId,
	tag,
	open,
	onDone,
}: {
	botId: string
	tag: Tag
	open: boolean
	onDone: () => void
}) {
	const refresh = useRefresh(botId)
	const [search, setSearch] = useState("")
	const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
	const query = useDebounced(search.trim(), 300)
	const filter = query ? { search: query } : {}

	const people = useInfiniteQuery({
		queryKey: [...audienceKeys.list(botId, filter), "add-to-group"],
		queryFn: ({ pageParam }) =>
			listAudience(botId, filter, { skip: pageParam, take: PAGE_SIZE, sort: "name" }),
		initialPageParam: 0,
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
			return loaded < last.total ? loaded : undefined
		},
		placeholderData: keepPreviousData,
		enabled: open,
	})
	const items = people.data?.pages.flatMap((page) => page.items) ?? []
	const total = people.data?.pages[0]?.total ?? 0

	const add = useMutation({
		mutationFn: () => bulkTagMembers(botId, tag.id, "add", { sessionIds: [...picked] }),
		onSuccess: async ({ changed }) => {
			await refresh()
			toast.success(
				changed === 0
					? "Все отмеченные уже в группе"
					: `Добавлено в «${tag.name}»: ${changed} ${plural(changed, PEOPLE)}`,
			)
			onDone()
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось добавить людей")),
	})

	const toggle = (id: string) => {
		const next = new Set(picked)
		if (next.has(id)) next.delete(id)
		else if (next.size < MAX_PICKED) next.add(id)
		else toast.info(`Отметить можно до ${MAX_PICKED} человек`)
		setPicked(next)
	}

	return (
		<div className="space-y-4">
			<div className="relative">
				<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					autoFocus
					aria-label="Поиск людей"
					placeholder="Имя, @username или ID"
					className="h-10 rounded-xl pl-9"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>
			</div>
			<div className="-mx-1 max-h-80 overflow-y-auto px-1">
				<LoadSwap loading={people.isLoading} id={query || "all"} autoHeight={false}>
					{people.isLoading ? (
						<ul aria-hidden="true" className="divide-y divide-border/60">
							{[0, 1, 2, 3].map((row) => (
								<li key={row} className="flex items-center gap-3 px-2 py-2">
									<Skeleton className="size-4 rounded" />
									<div className="flex-1 space-y-1.5">
										<Skeleton className="h-3.5 w-40 rounded" />
										<Skeleton className="h-3 w-28 rounded" />
									</div>
								</li>
							))}
						</ul>
					) : items.length === 0 ? (
						<p className="py-8 text-center text-sm text-muted-foreground">
							{query ? "Никого не нашли" : "У бота пока нет пользователей"}
						</p>
					) : (
						<ul className="divide-y divide-border/60">
							{items.map((member) => {
								const name =
									[member.firstName, member.lastName].filter(Boolean).join(" ") ||
									(member.username ? `@${member.username}` : member.telegramUserId)
								return (
									<li key={member.id}>
										<label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-accent/50">
											<Checkbox
												checked={picked.has(member.id)}
												onCheckedChange={() => toggle(member.id)}
												aria-label={`Выбрать ${name}`}
											/>
											<span className="min-w-0 flex-1">
												<span className="block truncate text-sm font-medium">{name}</span>
												<span className="block truncate text-xs text-muted-foreground">
													{member.username ? `@${member.username} · ` : ""}
													{member.telegramUserId}
												</span>
											</span>
										</label>
									</li>
								)
							})}
						</ul>
					)}
				</LoadSwap>
				{people.hasNextPage ? (
					<Button
						type="button"
						variant="ghost"
						className="mt-1 w-full rounded-xl"
						disabled={people.isFetchingNextPage}
						onClick={() => void people.fetchNextPage()}
					>
						{people.isFetchingNextPage ? <Loader2 className="animate-spin" /> : null}
						Показать ещё (всего {total})
					</Button>
				) : null}
			</div>
			<DialogFooter className="items-center sm:justify-between">
				<span className="text-sm text-muted-foreground tabular-nums">Отмечено: {picked.size}</span>
				<Button
					type="button"
					disabled={picked.size === 0 || add.isPending}
					onClick={() => add.mutate()}
				>
					{add.isPending ? <Loader2 className="animate-spin" /> : null}
					Добавить в группу
				</Button>
			</DialogFooter>
		</div>
	)
}

/** Элементы списка: по строкам, пробелам, запятым и точкам с запятой. */
const splitList = (text: string) =>
	[...new Set(text.split(/[\s,;]+/).filter(Boolean))].slice(0, MAX_LISTED)

function ListPane({
	botId,
	tag,
	open,
	onDone,
}: {
	botId: string
	tag: Tag
	open: boolean
	onDone: () => void
}) {
	const refresh = useRefresh(botId)
	const [text, setText] = useState("")
	const typed = useDebounced(text, 500)
	const identifiers = splitList(typed)
	const overLimit = text.split(/[\s,;]+/).filter(Boolean).length > MAX_LISTED

	const preview = useQuery({
		queryKey: ["bot-builder", "audience", botId, "list-preview", tag.id, identifiers],
		queryFn: () => tagMembersByList(botId, tag.id, identifiers, false),
		enabled: open && identifiers.length > 0,
		placeholderData: keepPreviousData,
		staleTime: 0,
	})
	const result = identifiers.length > 0 ? preview.data : undefined

	const add = useMutation({
		mutationFn: () => tagMembersByList(botId, tag.id, splitList(text), true),
		onSuccess: async ({ added, alreadyIn }) => {
			await refresh()
			toast.success(
				added === 0
					? "Все уже были в группе"
					: `Добавлено в «${tag.name}»: ${added} ${plural(added, PEOPLE)}${
							alreadyIn > 0 ? `, ${alreadyIn} уже были` : ""
						}`,
			)
			onDone()
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось добавить людей")),
	})

	const missing = [...(result?.notFound ?? []), ...(result?.unreadable ?? [])]
	const pending = text.trim() !== typed.trim() || preview.isFetching
	const fresh = result ? result.found - result.alreadyIn : 0

	return (
		<div className="space-y-4">
			<Textarea
				autoFocus
				aria-label="Список людей"
				placeholder={"@username\nhttps://t.me/username\n123456789"}
				className="min-h-36 rounded-xl font-mono text-sm"
				value={text}
				onChange={(event) => setText(event.target.value)}
			/>
			{overLimit ? (
				<p className="text-xs text-amber-300">
					За раз принимаем до {MAX_LISTED} человек, остальных обрежем.
				</p>
			) : null}
			<Reveal show={!!result}>
				<div className="space-y-2 rounded-xl border border-border bg-card/60 p-3 text-sm">
					<p className="tabular-nums">
						Нашли: <span className="font-medium">{result?.found ?? 0}</span>
						{result && result.alreadyIn > 0 ? (
							<span className="text-muted-foreground">
								, из них уже в группе {result.alreadyIn}
							</span>
						) : null}
					</p>
					{missing.length > 0 ? (
						<div>
							<p className="text-amber-300 tabular-nums">Не нашли: {missing.length}</p>
							<p className="mt-1 max-h-20 overflow-y-auto font-mono text-xs break-all text-muted-foreground">
								{missing.join(", ")}
							</p>
						</div>
					) : null}
				</div>
			</Reveal>
			<DialogFooter className="items-center sm:justify-between">
				<span className="text-sm text-muted-foreground tabular-nums">
					{identifiers.length > 0 ? `В списке: ${identifiers.length}` : "Список пуст"}
				</span>
				<Button
					type="button"
					disabled={!result || fresh === 0 || pending || add.isPending}
					onClick={() => add.mutate()}
				>
					{add.isPending || pending ? <Loader2 className="animate-spin" /> : null}
					{fresh > 0 ? `Добавить ${fresh}` : "Добавить в группу"}
				</Button>
			</DialogFooter>
		</div>
	)
}
